<script setup lang="ts">
/**
 * design-system.md -> Components -> Field. Label above (label role), the control in the default
 * slot, hint or error below (caption role). Wires up `id`/`aria-describedby`/`aria-invalid` and
 * hands them to the control via a scoped default slot, so the control (TextInput, SelectInput, or
 * anything else) stays a plain, reusable primitive that doesn't need to know about Field:
 *
 *   <Field id="wh-url" label="Endpoint URL" error="Use a public HTTPS address.">
 *     <template #default="{ id, describedBy, invalid }">
 *       <TextInput :id="id" v-model="url" mono :aria-describedby="describedBy" :aria-invalid="invalid" />
 *     </template>
 *   </Field>
 *
 * `optional` renders the "Optional" marker beside the label (design-system.md's create-tenant
 * domain field); the `labelTrailing` slot replaces it with anything else beside the label instead.
 */
import { computed } from 'vue';
import { CircleAlert } from '@lucide/vue';

const props = defineProps<{
  id: string;
  label: string;
  hint?: string;
  error?: string;
  optional?: boolean;
}>();

const describedBy = computed(() => {
  if (props.error) return `${props.id}-error`;
  if (props.hint) return `${props.id}-hint`;
  return undefined;
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <label :for="id" class="type-label flex items-baseline gap-2 text-ink">
      {{ label }}
      <slot name="labelTrailing">
        <span v-if="optional" class="type-caption text-ink-3">Optional</span>
      </slot>
    </label>

    <slot :id="id" :described-by="describedBy" :invalid="!!error" />

    <span v-if="error" :id="`${id}-error`" class="type-caption flex items-center gap-1.5 text-danger-fg">
      <CircleAlert :size="14" :stroke-width="2" aria-hidden="true" />
      {{ error }}
    </span>
    <span v-else-if="hint" :id="`${id}-hint`" class="type-caption text-ink-3">{{ hint }}</span>
  </div>
</template>
